'use client';

import React, { useEffect, useRef } from 'react';

const categories = [
  {
    id: 'hogar',
    label: 'Hogar & Ferretería',
    emoji: '🏠',
    desc: 'Muebles, electrodomésticos, herramientas y todo para tu hogar cerca de ti.',
    count: '320+ negocios',
    className: 'cat-hogar',
    span: 'col-span-2',
  },
  {
    id: 'comida',
    label: 'Comida & Restaurantes',
    emoji: '🍔',
    desc: 'Desde areperas hasta restaurantes gourmet. Encuentra dónde comer ahora.',
    count: '580+ locales',
    className: 'cat-comida',
    span: 'col-span-1',
  },
  {
    id: 'salud',
    label: 'Salud & Bienestar',
    emoji: '💊',
    desc: 'Farmacias, clínicas y centros de bienestar con disponibilidad confirmada.',
    count: '210+ centros',
    className: 'cat-salud',
    span: 'col-span-1',
  },
  {
    id: 'tech',
    label: 'Tecnología',
    emoji: '💻',
    desc: 'Equipos, accesorios y servicio técnico. Todo lo tech en tu zona.',
    count: '190+ tiendas',
    className: 'cat-tech',
    span: 'col-span-1',
  },
  {
    id: 'vehiculos',
    label: 'Vehículos & Repuestos',
    emoji: '🚗',
    desc: 'Repuestos, talleres y concesionarios. Tu vehículo en las mejores manos.',
    count: '260+ talleres',
    className: 'cat-vehiculos',
    span: 'col-span-2',
  },
  {
    id: 'moda',
    label: 'Moda & Accesorios',
    emoji: '👗',
    desc: 'Ropa, calzado y accesorios de las mejores tiendas locales.',
    count: '430+ boutiques',
    className: 'cat-moda',
    span: 'col-span-1',
  },
  {
    id: 'servicios',
    label: 'Servicios Profesionales',
    emoji: '🔧',
    desc: 'Plomeros, electricistas, diseñadores y más. Servicios verificados.',
    count: '340+ profesionales',
    className: 'cat-servicios',
    span: 'col-span-1',
  },
  {
    id: 'mascotas',
    label: 'Mascotas & Veterinarias',
    emoji: '🐾',
    desc: 'Todo para tu mascota: alimento, accesorios y atención veterinaria.',
    count: '120+ veterinarias',
    className: 'cat-mascotas',
    span: 'col-span-1',
  },
];

export default function CategoriesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const hiddenEls = entry.target.querySelectorAll('.reveal-hidden');
            hiddenEls.forEach((el, i) => {
              setTimeout(() => el.classList.add('reveal-visible'), i * 60);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="categorias" ref={sectionRef} className="py-32 relative">
      <div className="absolute inset-0 hero-glow opacity-50" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16">
          <div className="space-y-4 reveal-hidden">
            <p className="text-[10px] font-black uppercase tracking-[0.5em] text-primary">Categorías</p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
              Todo lo que necesitas,
              <br />
              <span className="gradient-text">todo aquí cerca</span>
            </h2>
          </div>
          <div className="reveal-hidden lg:max-w-sm space-y-4">
            <p className="text-muted text-lg leading-relaxed">
              8 categorías, miles de productos y servicios verificados. Búsca por nombre, categoría o ubicación.
            </p>
            <a
              href="#registro"
              className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-primary hover:text-primary-light transition-colors"
            >
              Ver todas las categorías
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        {/* Asymmetric Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories?.map((cat, i) => (
            <div
              key={cat?.id}
              className={`reveal-hidden bento-card ${cat?.className} ${
                cat?.span === 'col-span-2' ? 'md:col-span-2' : ''
              } rounded-[28px] border p-7 cursor-pointer group relative overflow-hidden`}
              style={{ minHeight: cat?.span === 'col-span-2' ? '180px' : '200px' }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[28px]"
                style={{ background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.04) 0%, transparent 70%)' }}
              />

              <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{cat?.emoji}</span>
                      <h3 className="text-lg font-bold text-white leading-tight">{cat?.label}</h3>
                    </div>
                    <p className="text-sm text-muted leading-relaxed max-w-xs">{cat?.desc}</p>
                  </div>
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest bg-white/5 text-white/60">
                    <div className="w-1 h-1 rounded-full bg-primary animate-pulse" />
                    {cat?.count}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}